import {
  Alert,
  Button,
  Collapse,
  ConfirmDialog,
  Divider,
  Group,
  iconSizes,
  Loader,
  Stack,
  Text,
  Title,
  toast,
} from '@common/ui'
import { IconPlus, IconTrash } from '@common/ui/icons'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { com, kotlin, Nullable } from '@eidu/web'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import SortableFieldRow from './SortableFieldRow'
import useEntityTypes, { useSortedEntityTypes } from '../../io/useEntityTypes'
import { useFetchEntityType } from '../../io/useEntityType'
import { useCreateEntityType, useDeleteEntityType, useModifyEntityType } from '../../io/useEntityTypeMutations'
import FieldData, {
  ExistingFieldData,
  fieldDataFromExistingField,
  fieldDataFromNewField,
} from '../../domain/entity/FieldData'
import TrimmedTextInput from '../../components/input/TrimmedTextInput'
import LabelInput from '../../components/input/LabelInput'
import KindInput from '../../components/input/KindInput'
import { createEntityLabel, labelLineToString, stringToLabelLine } from '../../components/misc/LabelUtil'
import {
  fieldOrderChanged as computeFieldOrderChanged,
  prepareFieldNameUpdates,
  prepareFieldOrder,
  prepareFieldsToCreate,
  prepareFieldsToDelete,
} from '../../util/entityType/updateHelpers'
import ConfirmationDialog from '../../components/layout/ConfirmationDialog'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import Field = com.eidu.entity.field.Field
import FieldType = com.eidu.entity.field.FieldType
import fieldIdFromString = com.eidu.entity.field.fieldIdFromString
import EntityTypeValidator = com.eidu.entity.validation.EntityTypeValidator
import TypeValidationError = com.eidu.entity.validation.TypeValidationError
import LabelTypeValidationError = com.eidu.entity.validation.LabelTypeValidationError
import KtList = kotlin.collections.KtList

type ManageEntityTypeFormProps = {
  entityTypeId: EntityTypeId | undefined
  entityType: EntityType | null
}

type ValidationState = {
  nameErrors?: readonly TypeValidationError[]
  fieldErrors?: Nullable<readonly TypeValidationError[]>[]
  primaryLabelErrors?: readonly TypeValidationError[]
  secondaryLabelErrors?: readonly TypeValidationError[]
  formError?: string
}

const fieldRowId = (field: FieldData): string => field.field.id.toString()

const createNewFieldData = (existingFields: readonly FieldData[]): FieldData => {
  const existingNames = new Set(existingFields.map((field) => field.field.name))
  let counter = existingFields.length + 1
  let fieldName = `Field ${counter}`
  while (existingNames.has(fieldName)) {
    counter += 1
    fieldName = `Field ${counter}`
  }
  const newField = Field.createSimple(fieldIdFromString(crypto.randomUUID()), fieldName, FieldType.Text, false, null)
  return fieldDataFromNewField(newField, existingFields.length)
}

const validateLabelLine = (
  value: string,
  fields: readonly FieldData[],
  allowReferences: boolean
): readonly TypeValidationError[] | undefined => {
  if (value.trim() === '') return undefined
  const labelLine = stringToLabelLine(value, fields)
  if (!labelLine) return [new LabelTypeValidationError('Label contains invalid field references')]
  return EntityTypeValidator.validateLabelLineWithFieldsList(
    labelLine,
    KtList.fromJsArray(fields.map((it) => it.field)),
    allowReferences
  )?.asJsReadonlyArrayView()
}

const validateState = (
  entityTypeName: string,
  fields: readonly FieldData[],
  primaryLabel: string,
  secondaryLabel: string
): ValidationState => {
  const allFields = fields.map((field) => field.field)
  const nameErrors = EntityTypeValidator.validateNameString(entityTypeName)?.asJsReadonlyArrayView()
  const fieldValidationResults = fields.map(
    (field) =>
      EntityTypeValidator.validateField(field.field, KtList.fromJsArray(allFields))?.asJsReadonlyArrayView() ?? null
  ) as Nullable<readonly TypeValidationError[]>[]

  const primaryLabelErrors = validateLabelLine(primaryLabel, fields, false)
  const secondaryLabelErrors = validateLabelLine(secondaryLabel, fields, true)

  let formError: string | undefined
  if (fields.length === 0) formError = 'At least one field is required.'
  else if (primaryLabel.trim() === '') formError = 'Primary label is required.'

  return { nameErrors, fieldErrors: fieldValidationResults, primaryLabelErrors, secondaryLabelErrors, formError }
}

type InitialFormState = {
  entityTypeName: string
  primaryLabel: string
  secondaryLabel: string
  kind: EntityTypeKind
  fields: readonly FieldData[]
  originalFields: readonly FieldData[]
}

const computeInitialFormState = (entityType: EntityType | null): InitialFormState => {
  if (!entityType) {
    return {
      entityTypeName: '',
      primaryLabel: '',
      secondaryLabel: '',
      kind: EntityTypeKind.Companion.Generic,
      fields: [],
      originalFields: [],
    }
  }
  const existingFieldData = entityType.fields
    .asJsReadonlyArrayView()
    .map((field) => fieldDataFromExistingField(field, undefined)) as readonly ExistingFieldData[]
  const fieldList = entityType.fields.asJsReadonlyArrayView()
  return {
    entityTypeName: entityType.name,
    primaryLabel: labelLineToString(entityType.label.primary, fieldList),
    secondaryLabel: entityType.label.secondary?.let((line) => labelLineToString(line, fieldList)) ?? '',
    kind: entityType.kind,
    fields: existingFieldData,
    originalFields: existingFieldData,
  }
}

const ManageEntityTypeForm = ({ entityTypeId, entityType }: ManageEntityTypeFormProps) => {
  const navigate = useNavigate()

  const { error: typesError } = useEntityTypes()
  const sortedEntityTypes = useSortedEntityTypes()
  const fetchEntityType = useFetchEntityType()
  const createMutation = useCreateEntityType()
  const modifyMutation = useModifyEntityType()
  const deleteMutation = useDeleteEntityType()

  const repositoryError = typesError ? String(typesError) : undefined

  const [initial] = useState(() => computeInitialFormState(entityType))
  const [entityTypeName, setEntityTypeName] = useState(initial.entityTypeName)
  const [primaryLabel, setPrimaryLabel] = useState(initial.primaryLabel)
  const [secondaryLabel, setSecondaryLabel] = useState(initial.secondaryLabel)
  const [kind, setKind] = useState<EntityTypeKind>(initial.kind)
  const [fields, setFields] = useState<readonly FieldData[]>(initial.fields)
  const [originalFields] = useState<readonly FieldData[]>(initial.originalFields)
  const [validation, setValidation] = useState<ValidationState>({})
  const [submitting, setSubmitting] = useState(false)
  const [fieldPendingDeletionIndex, setFieldPendingDeletionIndex] = useState<number | null>(null)

  const availableEntityTypes = sortedEntityTypes ?? []

  const updateFieldAt = async (index: number, update: (field: FieldData) => FieldData) => {
    const updated = fields.map((field, currentIndex) => (currentIndex === index ? update(field) : field))
    setFields(updated)
  }

  const handleFieldTypeChange = async (index: number, typeName: string) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withType(FieldType.valueOf(typeName)),
    }))
  }

  const handleFieldRequiredChange = async (index: number) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withRequired(!field.field.required),
    }))
  }

  const handleFieldReferenceChange = async (index: number, referenceTypes: readonly EntityTypeId[]) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withValidReferenceTypes(
        referenceTypes.length > 0 ? kotlin.collections.KtSet.fromJsSet(new Set(referenceTypes)) : undefined
      ),
    }))
  }

  const handleFieldNameChange = async (index: number, newName: string) => {
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withName(newName),
    }))
  }

  const addField = () => {
    setFields((current) => {
      const next = [...current, createNewFieldData(current)]
      if (!entityTypeId && primaryLabel.trim() === '') {
        setPrimaryLabel(`{${next[0].field.name}}`)
      }
      return next
    })
  }

  const removeField = (index: number) => {
    setFields((current) => current.filter((_, currentIndex) => currentIndex !== index))
  }

  const handleFieldReorder = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setFields((current) => {
      const oldIndex = current.findIndex((field) => fieldRowId(field) === active.id)
      const newIndex = current.findIndex((field) => fieldRowId(field) === over.id)
      if (oldIndex < 0 || newIndex < 0) return current
      return arrayMove([...current], oldIndex, newIndex)
    })
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleFieldRemoval = (index: number) => {
    const field = fields[index]
    if (!field) return
    if (field.existing) {
      setFieldPendingDeletionIndex(index)
      return
    }
    removeField(index)
  }

  const closeFieldDeletionDialog = () => setFieldPendingDeletionIndex(null)

  const confirmFieldDeletion = () => {
    if (fieldPendingDeletionIndex === null) return
    removeField(fieldPendingDeletionIndex)
    setFieldPendingDeletionIndex(null)
  }

  const deleteType = async () => {
    if (!entityTypeId) return
    const type = await fetchEntityType(entityTypeId)
    if (type === null) {
      toast.negative('Type not found')
      return
    }
    setSubmitting(true)
    try {
      await deleteMutation.mutateAsync(entityTypeId)
      toast.positive(`Deleted type ${type.name}`)
      navigate('/types')
    } catch (_error) {
      toast.negative('Failed to delete type')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    const currentValidation = validateState(entityTypeName, fields, primaryLabel, secondaryLabel)
    setValidation(currentValidation)
    if (
      currentValidation.formError ||
      currentValidation.nameErrors ||
      currentValidation.primaryLabelErrors ||
      currentValidation.secondaryLabelErrors ||
      currentValidation.fieldErrors?.some((it) => it && it.length > 0)
    )
      return

    const label = createEntityLabel({ primaryLabel, secondaryLabel, fields })
    const fieldsToCreate = prepareFieldsToCreate(fields)
    const fieldsToUpdate = prepareFieldNameUpdates(fields, originalFields)
    const fieldsToDelete = prepareFieldsToDelete(fields, originalFields)
    const fieldOrder = prepareFieldOrder(fields)
    const fieldOrderChanged = computeFieldOrderChanged(fields, originalFields)

    setSubmitting(true)
    try {
      if (entityTypeId) {
        await modifyMutation.mutateAsync({
          id: entityTypeId,
          entityTypeName: entityTypeName !== entityType?.name ? entityTypeName : undefined,
          label,
          fieldsToCreate,
          fieldsToUpdate,
          fieldsToDelete,
          fieldOrder,
          fieldOrderChanged,
        })
        toast.positive('Type saved')
      } else {
        if (!label) {
          toast.negative('Primary label is required')
          setSubmitting(false)
          return
        }
        await createMutation.mutateAsync({ name: entityTypeName, fieldsToCreate, label, kind })
        toast.positive('Type created')
      }
      navigate(-1)
    } catch (_error) {
      toast.negative('Failed to save type')
    } finally {
      setSubmitting(false)
    }
  }

  const pendingDeletionFieldName =
    fieldPendingDeletionIndex !== null ? fields[fieldPendingDeletionIndex]?.field.name ?? 'this field' : 'this field'

  return (
    <>
      <Stack gap="lg" p="lg">
        <Group justify="space-between">
          <Title order={2}>{entityTypeId ? 'Edit Type' : 'Create Type'}</Title>
          {entityTypeId && (
            <ConfirmationDialog
              title="Are you sure you want to delete this type? This action cannot be undone."
              content={
                <Group gap="xs">
                  <IconTrash size={iconSizes.sm} stroke={1.5} />
                  <Text>Delete type</Text>
                </Group>
              }
              onConfirm={deleteType}
            />
          )}
        </Group>

        <Stack gap="md">
          <Title order={4}>Name</Title>
          <TrimmedTextInput
            required
            label="Type name"
            value={entityTypeName}
            onChange={(event) => setEntityTypeName(event.target.value)}
            error={validation.nameErrors?.map((error) => error.message).join(' • ')}
          />
        </Stack>

        <Stack gap="md">
          <Title order={4}>Kind</Title>
          <KindInput kind={kind} setKind={setKind} editable={!entityTypeId} />
        </Stack>

        <Divider />

        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={3}>Fields</Title>
            <Button leftSection={<IconPlus size={iconSizes.sm} stroke={1.5} />} variant="outline" onClick={addField}>
              Add field
            </Button>
          </Group>

          {fields.length === 0 && <Text c="dimmed">Add at least one field to define the type.</Text>}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFieldReorder}>
            <SortableContext items={fields.map((field) => fieldRowId(field))} strategy={verticalListSortingStrategy}>
              <Stack gap="lg">
                {fields.map((fieldData, index) => (
                  <SortableFieldRow
                    key={fieldRowId(fieldData)}
                    id={fieldRowId(fieldData)}
                    index={index}
                    fieldData={fieldData}
                    availableEntityTypes={availableEntityTypes}
                    submitting={submitting}
                    errors={validation.fieldErrors?.[index] ?? undefined}
                    onRemove={() => handleFieldRemoval(index)}
                    onFieldTypeChange={(typeName) => handleFieldTypeChange(index, typeName)}
                    onRequiredChange={() => handleFieldRequiredChange(index)}
                    onReferenceTypesChange={(types) => handleFieldReferenceChange(index, types)}
                    onFieldNameChange={(newName) => handleFieldNameChange(index, newName)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Stack>

        <Divider />

        <LabelInput
          fields={fields}
          primaryLabel={primaryLabel}
          setPrimaryLabel={setPrimaryLabel}
          primaryLabelErrors={validation.primaryLabelErrors}
          secondaryLabel={secondaryLabel}
          setSecondaryLabel={setSecondaryLabel}
          secondaryLabelErrors={validation.secondaryLabelErrors}
          primaryLabelEditable
          secondaryLabelEditable
        />

        <Collapse expanded={!!validation.formError}>
          <Alert severity="negative">{validation.formError}</Alert>
        </Collapse>

        <Collapse expanded={!!repositoryError}>
          <Alert severity="negative">{repositoryError}</Alert>
        </Collapse>

        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={() => navigate(-1)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            leftSection={submitting ? <Loader size="xs" /> : undefined}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </Group>
      </Stack>
      <ConfirmDialog
        opened={fieldPendingDeletionIndex !== null}
        title="Confirm your action"
        message={`Deleting the field "${pendingDeletionFieldName}" will remove it from this type. This action cannot be undone.`}
        onConfirm={confirmFieldDeletion}
        confirmLabel="Delete field"
        onCancel={closeFieldDeletionDialog}
        destructive
      />
    </>
  )
}

export default ManageEntityTypeForm
